import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

// PrimeNG Imports
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';

// Local Imports
import { SessionManagerService } from '../../services/session-manager.service';
import { Session, SessionCreate, SessionCreateResponse } from '../../models/session.model';

@Component({
    selector: 'app-session-create-dialog',
    templateUrl: './session-create-dialog.component.html',
    styleUrls: ['./session-create-dialog.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        DialogModule,
        ButtonModule,
        InputTextModule,
        SelectModule
    ]
})
export class SessionCreateDialogComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() session: Session | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() sessionSaved = new EventEmitter<SessionCreateResponse>();

  sessionForm!: FormGroup;
  loading = false;
  isEdit = false;

  constructor(
    private fb: FormBuilder,
    private sessionService: SessionManagerService,
    private messageService: MessageService
  ) {
    this.initForm();
  }

  ngOnInit() {
    if (this.session) {
      this.isEdit = true;
      this.populateForm();
    }
  }

  initForm() {
    this.sessionForm = this.fb.group({
      sessionId: [this.sessionService.generateSessionId(), [Validators.required, Validators.minLength(4), this.sessionIdValidator]]
    });
  }

  // Custom validator for session ID
  sessionIdValidator = (control: any) => {
    if (!control.value) return null;
    
    // Allow only alphanumeric characters, hyphens, and underscores
    const validPattern = /^[a-zA-Z0-9_-]+$/;
    if (!validPattern.test(control.value)) {
      return { invalidCharacters: true };
    }
    
    return null;
  };

  populateForm() {
    if (this.session) {
      this.sessionForm.patchValue({
        sessionId: this.session.sessionId
      });
    }
  }

  onSave() {
    if (this.sessionForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.loading = true;
    const formValue = this.sessionForm.value;

    // Only create new sessions for now (editing not supported by current API)
    const sessionData: SessionCreate = {
      sessionId: formValue.sessionId
    };

    this.sessionService.createSession(sessionData)
      .subscribe({
        next: (newSession) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Session '${newSession.sessionId}' created successfully!`
          });
          this.sessionSaved.emit(newSession);
          this.onCancel();
          this.loading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create session. Session ID might already exist.'
          });
          this.loading = false;
        }
      });
  }

  onCancel() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.sessionForm.reset();
    this.session = null;
    this.isEdit = false;
    this.initForm();
  }

  private markFormGroupTouched() {
    Object.keys(this.sessionForm.controls).forEach(key => {
      const control = this.sessionForm.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.sessionForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.sessionForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
      }
      if (field.errors['minlength']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['invalidCharacters']) {
        return 'Session ID can only contain letters, numbers, hyphens, and underscores';
      }
    }
    return '';
  }

  // Handle input to convert spaces to hyphens and filter invalid characters
  onSessionIdInput(event: any) {
    let value = event.target.value;
    
    // Convert spaces to hyphens
    value = value.replace(/\s+/g, '-');
    
    // Remove any invalid characters (keep only alphanumeric, hyphens, and underscores)
    value = value.replace(/[^a-zA-Z0-9_-]/g, '');
    
    // Update the form control value
    this.sessionForm.patchValue({ sessionId: value });
  }

  generateNewSessionId() {
    this.sessionForm.patchValue({
      sessionId: this.sessionService.generateSessionId()
    });
  }
}
